import { useMemo } from "react";
import { Calendar, MapPin, Swords, Trophy } from "lucide-react";

import { Badge, Button, Sheet, useConfirm } from "@/ui";
import { formatEventDate } from "@/lib/format";
import {
  useAcceptChallenge,
  useCancelChallenge,
  useChallenges,
  useDeclineChallenge,
  useMyChallenges,
  useReportChallenge,
} from "@/features/queries";
import { useAuth } from "@/contexts/AuthContext";
import type { Challenge, ChallengeStatus } from "@/types/api";

interface Props {
  /** ID of the challenge to show, or null for closed state. */
  challengeId: number | null;
  /** Fallback snapshot used for the first paint while the list cache is
   *  still loading — keeps the sheet from flashing empty. */
  fallback?: Challenge | null;
  onClose: () => void;
}

const STATUS_LABEL: Record<ChallengeStatus, string> = {
  pending: "Ожидает",
  accepted: "Принят",
  declined: "Отклонён",
  cancelled: "Отменён",
  completed: "Сыгран",
};

const STATUS_KIND: Record<ChallengeStatus, "brand" | "success" | "neutral"> = {
  pending: "brand",
  accepted: "success",
  declined: "neutral",
  cancelled: "neutral",
  completed: "neutral",
};

function briefName(u: Challenge["challenger"]): string {
  return (
    [u.first_name, u.last_name].filter(Boolean).join(" ") ||
    u.username ||
    `игрок #${u.id}`
  );
}

export function ChallengeDetailSheet({ challengeId, fallback, onClose }: Props) {
  return (
    <Sheet open={challengeId !== null} onClose={onClose}>
      {challengeId !== null && (
        <ChallengeDetail
          challengeId={challengeId}
          fallback={fallback ?? null}
          onClose={onClose}
        />
      )}
    </Sheet>
  );
}

function ChallengeDetail({
  challengeId,
  fallback,
  onClose,
}: {
  challengeId: number;
  fallback: Challenge | null;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const confirm = useConfirm();

  // Always pull fresh state from whichever cache has it. The public list
  // (useChallenges) and the "mine" list (useMyChallenges) share the same
  // rows for challenges involving the current user, but the profile uses
  // the "mine" list while the events page uses the full list — merge.
  const allQ = useChallenges();
  const myQ = useMyChallenges();

  const challenge = useMemo<Challenge | null>(() => {
    const findIn = (arr?: Challenge[]) =>
      arr?.find((c) => c.id === challengeId) ?? null;
    return findIn(allQ.data) ?? findIn(myQ.data) ?? fallback;
  }, [allQ.data, myQ.data, fallback, challengeId]);

  const accept = useAcceptChallenge();
  const decline = useDeclineChallenge();
  const cancel = useCancelChallenge();
  const report = useReportChallenge();

  if (!user || !challenge) return null;

  const iAmChallenger = user.id === challenge.challenger.id;
  const iAmChallenged = user.id === challenge.challenged.id;
  const iAmParticipant = iAmChallenger || iAmChallenged;

  const when = challenge.scheduled_at
    ? formatEventDate(challenge.scheduled_at)
    : "сейчас";

  const myReportedWinner = iAmChallenger
    ? challenge.challenger_reported_winner_id
    : challenge.challenged_reported_winner_id;
  const alreadyReported = myReportedWinner != null;

  const pending = accept.isPending || decline.isPending || cancel.isPending || report.isPending;
  const error =
    (accept.error || decline.error || cancel.error || report.error) as Error | null;

  // Per-click close — if the mutation completes (completion = match has
  // both sides agreeing → rating applied), close the sheet too.
  const runReport = (winner_user_id: number) => {
    report.mutate(
      { id: challenge.id, winner_user_id },
      {
        onSuccess: (fresh) => {
          if (fresh.status === "completed") onClose();
        },
      },
    );
  };

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Badge kind={STATUS_KIND[challenge.status]}>{STATUS_LABEL[challenge.status]}</Badge>
        <Badge kind="neutral">Вызов</Badge>
      </div>

      <h2 className="h2" style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
        {briefName(challenge.challenger)}
        <Swords size={20} color="var(--brand-orange)" />
        {briefName(challenge.challenged)}
      </h2>

      <div
        style={{
          background: "var(--bg-secondary)",
          borderRadius: "var(--r-md)",
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <Row icon={<Calendar size={16} />} label="Когда">{when}</Row>
        {challenge.spot_name && (
          <Row icon={<MapPin size={16} />} label="Где">
            {challenge.spot_latitude != null && challenge.spot_longitude != null ? (
              <a
                href={`https://yandex.ru/maps/?pt=${challenge.spot_longitude},${challenge.spot_latitude}&z=16&l=map`}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--brand-orange)", fontWeight: 500 }}
              >
                {challenge.spot_name}
                {challenge.spot_address ? (
                  <span style={{ color: "var(--fg3)", fontWeight: 400 }}>
                    {" · "}{challenge.spot_address}
                  </span>
                ) : null}
              </a>
            ) : (
              <span>{challenge.spot_name}</span>
            )}
          </Row>
        )}
        {challenge.status === "completed" && challenge.winner_id != null && (
          <Row icon={<Trophy size={16} />} label="Победитель">
            {challenge.winner_id === challenge.challenger.id
              ? briefName(challenge.challenger)
              : briefName(challenge.challenged)}
          </Row>
        )}
      </div>

      {/* Challenged user with pending — Accept / Decline */}
      {challenge.status === "pending" && iAmChallenged && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Button
            fullWidth
            size="lg"
            onClick={() =>
              accept.mutate(challenge.id, { onSuccess: () => onClose() })
            }
            disabled={pending}
          >
            Принять вызов
          </Button>
          <Button
            fullWidth
            variant="ghost"
            onClick={() =>
              decline.mutate(challenge.id, { onSuccess: () => onClose() })
            }
            disabled={pending}
          >
            Отклонить
          </Button>
        </div>
      )}

      {/* Challenger with pending — Cancel */}
      {challenge.status === "pending" && iAmChallenger && (
        <Button
          fullWidth
          variant="ghost"
          onClick={async () => {
            const ok = await confirm({
              title: "Отменить вызов?",
              description: `Вызов на ${briefName(challenge.challenged)} будет отменён.`,
              confirmLabel: "Отменить",
              cancelLabel: "Назад",
              tone: "danger",
            });
            if (ok) {
              cancel.mutate(challenge.id, { onSuccess: () => onClose() });
            }
          }}
          disabled={pending}
        >
          Отменить вызов
        </Button>
      )}

      {/* Accepted + participant — Report result */}
      {challenge.status === "accepted" && iAmParticipant && (
        <>
          <div className="label" style={{ color: "var(--fg2)" }}>
            {alreadyReported ? "Ваш выбор отправлен" : "Кто победил?"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              fullWidth
              variant={myReportedWinner === user.id ? "primary" : "secondary"}
              onClick={() => runReport(user.id)}
              disabled={pending}
            >
              Я выиграл
            </Button>
            <Button
              fullWidth
              variant={
                myReportedWinner != null && myReportedWinner !== user.id
                  ? "primary"
                  : "secondary"
              }
              onClick={() => {
                const opponentId = iAmChallenger
                  ? challenge.challenged.id
                  : challenge.challenger.id;
                runReport(opponentId);
              }}
              disabled={pending}
            >
              Я проиграл
            </Button>
          </div>
          {alreadyReported && (
            <div
              style={{
                background: "var(--bg-secondary)",
                borderRadius: "var(--r-md)",
                padding: 10,
                fontSize: 13,
                color: "var(--fg2)",
              }}
            >
              Ждём подтверждения соперника. Когда оба укажут одного
              победителя — результат засчитается и пересчитается рейтинг.
            </div>
          )}
        </>
      )}

      {error && (
        <div
          style={{
            background: "rgba(223,63,64,0.1)",
            color: "var(--fg-danger)",
            padding: "8px 12px",
            borderRadius: "var(--r-md)",
            fontSize: 13,
          }}
        >
          {error.message}
        </div>
      )}

      {(challenge.status === "completed"
        || challenge.status === "declined"
        || challenge.status === "cancelled") && (
        <Button fullWidth variant="ghost" onClick={onClose}>
          Закрыть
        </Button>
      )}
    </div>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", gap: 10 }}>
      <span style={{ color: "var(--fg3)", marginTop: 2, flex: "none" }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="label" style={{ color: "var(--fg3)" }}>{label}</div>
        <div className="body" style={{ marginTop: 2 }}>{children}</div>
      </div>
    </div>
  );
}
